import React from 'react';
import { BuildingOfficeIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { ManagedProperty as BaseManagedProperty } from '../property/property-card/PropertyCardTypes';

type ManagedProperty = BaseManagedProperty & {
  firestoreDocId: string;
};

interface PropertyListProps {
  properties: ManagedProperty[];
  onSelect: (property: ManagedProperty) => void;
  className?: string;
}

const PropertyList: React.FC<PropertyListProps> = ({
  properties,
  onSelect,
  className = ''
}) => {
  if (properties.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <BuildingOfficeIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No Properties Yet
        </h3>
        <p className="text-gray-600 mb-6">
          Start managing rent by adding properties with tenants.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}
    >
      {properties.map((property) => (
        <div
          key={property.id}
          onClick={() => onSelect(property)}
          className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-all cursor-pointer hover:border-primary group"
        >
          {property.images && property.images.length > 0 ? (
            <img
              src={property.images[0]}
              alt={property.title}
              className="w-full h-48 object-cover rounded-lg mb-4"
            />
          ) : (
            <div className="w-full h-48 bg-gray-100 rounded-lg mb-4 flex items-center justify-center text-gray-400">
              <BuildingOfficeIcon className="w-12 h-12" />
            </div>
          )}

          <div className="flex justify-between items-start mb-2">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-primary transition-colors">
                {property.title}
              </h3>
              <p className="text-sm text-gray-600 flex items-center">
                <MapPinIcon className="w-4 h-4 mr-1" />
                {property.address || property.location}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center">
              <span className="font-medium text-gray-900 mr-1">
                {property.bedrooms}
              </span>{' '}
              Beds
            </div>
            <div className="flex items-center">
              <span className="font-medium text-gray-900 mr-1">
                {property.bathrooms}
              </span>{' '}
              Baths
            </div>
            <div className="flex items-center col-span-2">
              <span className="font-medium text-gray-900 mr-1">
                {property.type}
              </span>
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                {property.listingIntent === 'sale'
                  ? 'For Sale'
                  : property.listingIntent === 'share'
                    ? 'House Share'
                    : 'For Rent'}
              </span>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
            <span className="text-lg font-bold text-primary">
              {property.price != null &&
                (property.listingIntent === 'sale'
                  ? `€${property.price}`
                  : `€${property.price}/mo`)}
            </span>
            <span className="text-xs font-medium px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
              {property.status === 'managed' ? 'Managed' : property.status}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PropertyList;
